import * as Popover from '@radix-ui/react-popover'
import {
  addMonths,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfISOWeek,
  format,
  getISOWeek,
  getISOWeekYear,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfISOWeek,
} from 'date-fns'
import { nl } from 'date-fns/locale'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface PeriodPickerProps {
  mode: 'week' | 'day'
  /** Zichtbare tekst op de trigger, bijvoorbeeld "Week 31" of "Maandag 28 juli". */
  label: string
  /** De gekozen dag; in weekstand een dag uit de gekozen week. */
  selected: Date
  onPick: (date: Date) => void
}

const dayHeaders = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']

export function PeriodPicker({ mode, label, selected, onPick }: PeriodPickerProps) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => startOfMonth(selected))

  // De maand volgt de selectie zodra je de kalender opent: sluiten in november en later
  // heropenen moet niet nog steeds november tonen als je inmiddels in januari zit.
  function change(next: boolean) {
    if (next) setCursor(startOfMonth(selected))
    setOpen(next)
  }

  function pick(date: Date) {
    onPick(date)
    setOpen(false)
  }

  const weeks = eachWeekOfInterval(
    { start: startOfMonth(cursor), end: endOfMonth(cursor) },
    { weekStartsOn: 1 },
  )
  const selectedWeekStart = startOfISOWeek(selected)
  const today = new Date()

  return (
    <Popover.Root open={open} onOpenChange={change}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`${mode === 'week' ? 'Kies week' : 'Kies dag'}, nu ${label}`}
          className="btn-ghost h-8 gap-1 px-2 text-sm font-medium text-ink"
        >
          {label}
          <ChevronDown className="h-3.5 w-3.5 text-ink-faint" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          role="dialog"
          aria-label="Kalender"
          className="z-40 rounded-card border border-hairline bg-surface p-2 shadow-pop animate-fade-in"
        >
          <div className="flex items-center justify-between gap-2 px-1 pb-1.5">
            <button
              type="button"
              onClick={() => setCursor(addMonths(cursor, -1))}
              className="btn-ghost h-7 w-7 p-0"
              aria-label="Vorige maand"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span aria-live="polite" className="text-sm font-medium text-ink">
              {format(cursor, 'LLLL yyyy', { locale: nl })}
            </span>
            <button
              type="button"
              onClick={() => setCursor(addMonths(cursor, 1))}
              className="btn-ghost h-7 w-7 p-0"
              aria-label="Volgende maand"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-[2rem_repeat(7,2rem)] gap-y-0.5">
            <span className="label-caps flex h-7 items-center justify-center">wk</span>
            {dayHeaders.map((short) => (
              <span key={short} className="label-caps flex h-7 items-center justify-center">
                {short}
              </span>
            ))}
          </div>

          {weeks.map((weekStart) => {
            const days = eachDayOfInterval({ start: weekStart, end: endOfISOWeek(weekStart) })
            const weekNumber = getISOWeek(weekStart)
            const isSelectedWeek = isSameDay(weekStart, selectedWeekStart)

            if (mode === 'week') {
              return (
                <button
                  key={weekStart.toISOString()}
                  type="button"
                  onClick={() => pick(weekStart)}
                  data-week={`${getISOWeekYear(weekStart)}-W${String(weekNumber).padStart(2, '0')}`}
                  aria-label={`Week ${weekNumber}, ${format(weekStart, 'd MMMM yyyy', { locale: nl })}`}
                  aria-current={isSelectedWeek ? 'true' : undefined}
                  className={cn(
                    'grid w-full grid-cols-[2rem_repeat(7,2rem)] rounded-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
                    isSelectedWeek ? 'bg-accent-600 text-canvas' : 'hover:bg-fill-2',
                  )}
                >
                  <span
                    className={cn(
                      'num flex h-8 items-center justify-center text-xs',
                      isSelectedWeek ? 'text-canvas/70' : 'text-ink-faint',
                    )}
                  >
                    {weekNumber}
                  </span>
                  {days.map((day) => (
                    <span
                      key={day.toISOString()}
                      data-date={format(day, 'yyyy-MM-dd')}
                      className={cn(
                        'num flex h-8 items-center justify-center text-sm',
                        !isSelectedWeek && !isSameMonth(day, cursor) && 'text-ink-faint',
                        !isSelectedWeek && isSameDay(day, today) && 'font-semibold text-accent-700',
                      )}
                    >
                      {day.getDate()}
                    </span>
                  ))}
                </button>
              )
            }

            return (
              <div key={weekStart.toISOString()} className="grid grid-cols-[2rem_repeat(7,2rem)]">
                <span className="num flex h-8 items-center justify-center text-xs text-ink-faint">
                  {weekNumber}
                </span>
                {days.map((day) => {
                  const isSelected = isSameDay(day, selected)
                  const isToday = isSameDay(day, today)
                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => pick(day)}
                      data-date={format(day, 'yyyy-MM-dd')}
                      aria-label={format(day, 'EEEE d MMMM yyyy', { locale: nl })}
                      aria-current={isSelected ? 'date' : undefined}
                      className={cn(
                        'num flex h-8 items-center justify-center rounded-md text-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
                        isSelected
                          ? 'bg-accent-600 text-canvas'
                          : cn(
                              'hover:bg-fill-2',
                              !isSameMonth(day, cursor) && 'text-ink-faint',
                              isToday && 'font-semibold text-accent-700 ring-1 ring-accent-600/40',
                            ),
                      )}
                    >
                      {day.getDate()}
                    </button>
                  )
                })}
              </div>
            )
          })}

          <div className="mt-1.5 border-t border-hairline pt-1.5">
            <button
              type="button"
              onClick={() => pick(mode === 'week' ? startOfISOWeek(today) : today)}
              className="btn-ghost h-8 w-full text-sm"
            >
              {mode === 'week' ? 'Deze week' : 'Vandaag'}
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
